import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, LogOut } from 'lucide-react';
import { QUICK_LINKS } from '../../data/portalData';

const NAME_KEY = 'reset_member_name';

export function getMemberName() {
  if (typeof window === 'undefined') return '';
  return localStorage.getItem(NAME_KEY) || '';
}

export function setMemberName(name) {
  if (typeof window !== 'undefined') {
    localStorage.setItem(NAME_KEY, name);
  }
}

export function clearMemberName() {
  if (typeof window !== 'undefined') localStorage.removeItem(NAME_KEY);
}

export default function PortalLayout({ children }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [hasAdminToken, setHasAdminToken] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setName(getMemberName());
    setHasAdminToken(
      typeof window !== 'undefined' && Boolean(localStorage.getItem('reset_admin_token')),
    );
  }, [location]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const initial = (name || 'B').charAt(0).toUpperCase();

  const handleSignOut = () => {
    clearMemberName();
    window.location.href = '/portal';
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="portal-layout">
      <header className="border-b border-white/5 bg-[#0A0A0A]/85 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link
            to="/portal"
            className="flex items-center gap-3"
            data-testid="portal-brand-link"
          >
            <img
              src="https://customer-assets-4nw71qhi.emergentagent.net/job_analytics-live-1/artifacts/8w3x6659_brotherhood-reset-logo.jpg"
              alt="The Reset · The Brotherhood Circle"
              className="w-10 h-10 rounded-full object-cover border border-[#D4AF37]/40 flex-shrink-0"
              data-testid="portal-brand-logo"
            />
            <div>
              <p className="text-white font-cormorant text-lg leading-tight">
                The Reset
              </p>
              <p className="text-zinc-500 font-manrope text-[10px] uppercase tracking-widest">
                The Brotherhood Circle
              </p>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {QUICK_LINKS.slice(0, 6).map((l) => (
              <Link
                key={l.key}
                to={l.href}
                className={`px-3 py-1.5 rounded-full font-manrope text-sm transition-colors ${
                  location.pathname === l.href
                    ? 'bg-[#D4AF37]/15 text-[#D4AF37]'
                    : 'text-zinc-400 hover:text-white'
                }`}
                data-testid={`nav-link-${l.key}`}
              >
                {l.title}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {name ? (
              <div
                className="hidden sm:flex items-center gap-2 pr-1"
                data-testid="portal-user-badge"
              >
                <div className="w-8 h-8 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37] font-cormorant font-medium">
                  {initial}
                </div>
                <span
                  className="text-zinc-300 font-manrope text-sm"
                  data-testid="portal-user-name"
                >
                  {name.split(' ')[0]}
                </span>
                <button
                  onClick={handleSignOut}
                  className="ml-1 text-zinc-600 hover:text-red-400 transition-colors"
                  aria-label="Sign out"
                  data-testid="portal-signout-btn"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            ) : null}
            <button
              className="lg:hidden w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center"
              onClick={() => setOpen((o) => !o)}
              data-testid="portal-menu-toggle"
              aria-label="Menu"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-white/5 bg-[#0A0A0A]" data-testid="portal-mobile-menu">
            <nav className="max-w-7xl mx-auto px-5 py-4 grid grid-cols-1 gap-1">
              {QUICK_LINKS.map((l) => (
                <Link
                  key={l.key}
                  to={l.href}
                  className={`px-3 py-2.5 rounded-lg font-manrope text-sm transition-colors ${
                    location.pathname === l.href
                      ? 'bg-[#D4AF37]/15 text-[#D4AF37]'
                      : 'text-zinc-300 hover:bg-white/5'
                  }`}
                >
                  {l.title}
                </Link>
              ))}
              {name && (
                <button
                  onClick={handleSignOut}
                  className="px-3 py-2.5 rounded-lg font-manrope text-sm text-zinc-400 hover:text-red-400 text-left"
                  data-testid="portal-signout-mobile"
                >
                  Sign out
                </button>
              )}
            </nav>
          </div>
        )}
      </header>

      <main>{children}</main>

      <footer className="border-t border-white/5 py-8 px-6 mt-16">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-zinc-600 font-manrope text-xs">
            The Reset &middot; A year-round community where Black men reset their mind, body,
            purpose, finances, and legacy.
          </p>
          {hasAdminToken && (
            <p className="mt-3">
              <Link
                to="/admin"
                className="text-zinc-700 hover:text-[#D4AF37] font-manrope text-[10px] uppercase tracking-[0.3em] transition-colors"
                data-testid="portal-footer-admin-link"
              >
                Admin
              </Link>
            </p>
          )}
        </div>
      </footer>
    </div>
  );
}
